import { Color, createTheme, Palette, Theme } from '@mui/material';

import { createClamp } from '#shared';

import { commonMixins } from './mixins';

import type { CSSProperties } from '@mui/material/styles';
import type {} from '@mui/x-charts-pro/themeAugmentation';
import type {} from '@mui/x-data-grid-pro/themeAugmentation';
import type {} from '@mui/x-date-pickers-pro/themeAugmentation';

// import { ColorPartial } from '@mui/material/createPalette';
type CustomMixin = (
  palette: Palette,
  options?: {
    primaryOpacity?: number;
    secondaryOpacity?: number;
    prefix?: string;
  },
) => CSSProperties;
declare module '@mui/material/styles' {
  interface Mixins {
    severityStyles: CustomMixin;
    aircraftStatusStyles: CustomMixin;
    organStyles: CustomMixin;
    flightCategoryStyles: CustomMixin;
  }
}

declare module '@mui/material' {
  type VwRange =
    `${keyof Theme['breakpoints']['values']}-${keyof Theme['breakpoints']['values']}`;

  interface Theme {
    utils: {
      clamp: (min: number, max: number, vwRange?: VwRange) => string;
    };
  }

  interface ThemeOptions {
    utils?: Theme['utils'];
  }
}

declare module '@mui/material' {
  interface BreakpointOverrides {
    '2k': true;
    '4k': true;
  }

  interface Theme {
    columnSize: number;
    column: (multiplier: number) => number;
  }

  interface ThemeOptions {
    columnSize?: number;
  }

  interface TypographyVariants {
    red: React.CSSProperties;
    grey: React.CSSProperties;
    blue: React.CSSProperties;
    green: React.CSSProperties;
    violet: React.CSSProperties;
    yellow: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    red?: React.CSSProperties;
    grey?: React.CSSProperties;
    blue?: React.CSSProperties;
    green?: React.CSSProperties;
    violet?: React.CSSProperties;
    yellow?: React.CSSProperties;
  }

  interface Palette {
    blue: Color;
    mapBlue: Color;
    green: Color;
    violet: Color;
    orange: Color;
    mint: Color;
    yellow: Color;
    flightCategory: {
      IFR: string;
      MVFR: string;
      VFR: string;
      LIFR: string;
      NA: string;
    };
    organ: {
      liver: string;
      lungs: string;
      heart: string;
    };
    aircraftStatus: {
      available: string;
      notAvailable: string;
      maintenance: string;
      aog: string;
    };
    severity: {
      critical: string;
      major: string;
      minor: string;
      low: string;
    };
  }

  interface PaletteOptions {
    blue: Color;
    mapBlue: Color;
    green: Color;
    violet: Color;
    orange: Color;
    mint: Color;
    yellow: Color;
  }
}
// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    red: true;
    grey: true;
    blue: true;
    green: true;
    violet: true;
    yellow: true;
  }
}

export const defaultTheme: Theme = createTheme({
  spacing: 8,
  columnSize: 160,
  breakpoints: {
    values: {
      xs: 480,
      sm: 768,
      md: 1024,
      lg: 1440,
      xl: 1920,
      '2k': 2560, // Add a custom breakpoint for 2K displays
      '4k': 3840, // Add a custom breakpoint for 4K displays
    },
  },
  // Add common mixins
  mixins: commonMixins,
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          // Map the new variant to render a <h1> by default
          red: 'strong',
          grey: 'strong',
          blue: 'strong',
          green: 'strong',
          violet: 'strong',
          yellow: 'strong',
        },
      },
    },
    MuiChartsSurface: {
      styleOverrides: {
        root: {
          touchAction: 'auto',
        },
      },
    },
    // We don't need to include common components in the default theme
    // as they will be included in each specific theme
  },
  typography: {
    // fontFamily: `'Avenir Next', Arial, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto',
    // 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif`,
    fontFamily: '"Inter", sans-serif',
    red: {
      color: '#FF3844',
    },
    grey: {
      color: '#D1D9E4',
    },
  },
});

defaultTheme.utils = {
  clamp: createClamp(defaultTheme),
};

// Add column helper to theme instance
defaultTheme.column = (multiplier: number): number =>
  defaultTheme.columnSize * multiplier;
